import { glp1BlogPost } from "./glp1BlogPost";

export const blogPosts = [
  {
    slug: "gut-microbiome-why-your-gut-health-matters",
    title: "The Gut Microbiome: Why Your Gut Health Matters More Than You Think",
    excerpt:
      "Your gut microbiome influences immunity, metabolism, digestion and even mood. Learn why diversity matters and the simple habits that support gut health.",
    image: "/gut-microbiome-cover.png",
    category: "Gut Health",
    date: "2026-05-31",
    readingTime: "5 min read",
    author: "Iballa Martinez",
    callout:
      "A diverse gut microbiome is one of the strongest signs of a healthy gut, and small daily habits can make a meaningful difference.",
    sections: [
      {
        heading: "Why Diversity Is the Key to a Healthy Gut",
        body: [
          "Our large bowel is home to approximately 38 trillion bacteria, a number so vast it can be difficult to comprehend. This may sound alarming, as we often associate bacteria with infection and disease. However, these microorganisms, collectively known as the gut flora or microbiota, play a fundamental role in our health, influencing everything from our immune system and metabolism to our brain and digestive function.",
          "Research has identified over 1,000 different species of bacteria living in the human bowel, though each individual typically harbours between 100 and 150 of these species. Much like our DNA, this makes each person's gut microbiome entirely unique to them.",
          "The diversity of bacteria in your gut is a marker of optimal health. Studies have consistently shown that reduced microbial diversity is associated with a range of conditions including inflammatory bowel disease, obesity, type 2 diabetes, and autoimmune conditions such as coeliac disease, type 1 diabetes, eczema, and psoriasis.",
          "Research looking at the gut microbiota in people living with obesity has frequently identified dysbiosis, an imbalance in which harmful bacteria outnumber beneficial ones, as a common finding. On the flip side, a higher diversity of gut bacteria has been shown to be a strong indicator of a healthy gut and better overall health outcomes."
        ]
      },
      {
        heading: "What Does a Healthy Gut Actually Do?",
        body: [
          "Having a healthy gut goes far beyond keeping bowel movements regular or avoiding uncomfortable digestive symptoms. A well-functioning gut microbiome has been shown to have a beneficial impact on immune function, energy metabolism, hormone regulation and cardiovascular health.",
          "Much of this benefit comes down to what these bacteria actually do. When gut bacteria ferment undigestible food compounds, particularly dietary fibre, they produce substances called short-chain fatty acids as a by-product."
        ],
        list: [
          "Acetate is the most abundant short-chain fatty acid in the gut. It supports the growth of other beneficial bacteria, plays a role in fat and cholesterol metabolism, and is involved in appetite regulation.",
          "Propionate travels to the liver where it contributes to glucose metabolism and helps signal feelings of fullness and satiety.",
          "Butyrate is the primary energy source for the cells lining the colon. It plays a key role in glucose and energy metabolism and helps maintain the oxygen balance in the gut, which in turn helps prevent dysbiosis."
        ]
      },
      {
        heading: "The Gut-Brain Axis: Your Gut and Mind Are More Connected Than You Think",
        body: [
          "Beyond digestion and metabolism, another fascinating and well-established aspect of gut health is the gut-brain axis, the two-way communication network between the cognitive and emotional centres of the brain and the gut.",
          "If you have ever felt butterflies in your stomach when nervous or anxious, you have experienced the gut-brain axis in action.",
          "In practice, this means that mood and mental health can directly impact how your gut functions, and vice versa. A clear example of this is irritable bowel syndrome. IBS is frequently treated with therapies that target the brain such as cognitive behavioural therapy, gut-directed hypnotherapy, or antidepressants, which in many cases lead to a meaningful reduction in gut symptoms. Equally, nutritional approaches such as the low FODMAP diet, which directly targets gut symptoms, have been shown to improve emotional and psychological wellbeing."
        ]
      },
      {
        heading: "Taking Care of Your Gut Doesn't Have to Be Complicated",
        body: [
          "The inner workings of the gut and its far-reaching impact on our health can be complex. But looking after your gut health does not have to be. These practical habits can make a real difference."
        ],
        subsections: [
          {
            heading: "Eat a variety of plant-based foods",
            body: [
              "Eating a wide variety of plants is very powerful when it comes to gut health. It will help you reach the recommended daily intake of 30 grams of fibre, something that an estimated 90% of Europeans currently fall short of. Fibre keeps bowel movements regular and comfortable to pass, and it feeds the beneficial bacteria living in your gut."
            ],
            list: [
              "Aim to eat 30 different plants every week, including fruit, vegetables, legumes, grains, herbs and seeds.",
              "Include some fruit at breakfast.",
              "Aim to fill half your plate with vegetables at lunch and dinner.",
              "Choose wholegrain breakfast cereals such as oats or wheat biscuits.",
              "Switch white bread, crackers, wraps, pasta and rice to wholegrain or brown alternatives.",
              "Snack on fruit and/or a small handful of nuts.",
              "Sprinkle chia, flax, sesame or pumpkin seeds onto meals for an easy fibre boost.",
              "Aim for at least two meat-free days a week.",
              "Include legumes such as chickpeas, lentils or beans in your meals at least every other day."
            ]
          },
          {
            heading: "Stick to regular mealtimes",
            body: [
              "Regular meals are key to keeping your gut bacteria healthy. An irregular eating pattern can disrupt the health of your microbiota and lead to uncomfortable gut symptoms such as bloating and abdominal pain. Aim to have three meals a day at consistent times, along with one or two balanced, colourful snacks."
            ]
          },
          {
            heading: "Stay well hydrated",
            body: [
              "Drinking plenty of fluids, approximately eight cups per day, is essential for gut health. Hydration helps maintain the lining of the gut and keeps bowel movements soft and comfortable to pass. Tea, coffee and smoothies also count towards your fluid intake, not only water."
            ]
          },
          {
            heading: "Manage your stress levels",
            body: [
              "Managing stress is a very underrated aspect of gut health. Elevated stress levels can directly impact your gut health and trigger symptoms such as bloating, cramping, changes in bowel habits and abdominal pain. Mindfulness, breathing exercises, yoga, meditation or simply carving out time to rest can all support a healthier digestive system."
            ]
          },
          {
            heading: "Stay Active",
            body: [
              "Regular physical activity is another important pillar of gut health. Exercise helps stimulate the natural contractions of the intestines, keeping things moving through your digestive system and reducing the likelihood of constipation and bloating. Even a 30-minute walk most days of the week can have a meaningful impact."
            ]
          }
        ]
      },
      {
        heading: "When to Seek Medical Advice",
        body: [
          "While the habits outlined above can go a long way in supporting a healthy gut, it is important to listen to your body. If you experience any red flag symptoms, contact your doctor or healthcare provider promptly."
        ],
        list: [
          "Unexplained or unintentional weight loss",
          "Blood in your stool or rectal bleeding",
          "Persistent diarrhoea or constipation that does not improve",
          "Severe or worsening abdominal pain",
          "Difficulty swallowing",
          "Persistent nausea or vomiting",
          "A noticeable change in your bowel habits that lasts more than a few weeks",
          "Unexplained fatigue or anaemia",
          "Waking up in the middle of the night to have a bowel movement"
        ],
        bodyAfter: [
          "These symptoms can sometimes indicate an underlying condition that requires professional assessment and should never be ignored. If something feels off and it has been persisting for more than two to three weeks, trust your instincts and seek advice.",
          "The tips shared in this blog are intended to support general gut health and wellbeing. They are not a substitute for personalised medical or nutritional advice. If you would like tailored guidance on your diet and gut health, a registered dietitian can work with you to create a plan that fits your individual needs and health goals."
        ]
      }
    ],
    references: [
      {
        label:
          "Valdes, A.M., Walter, J., Segal, E. and Spector, T.D. (2018) 'Role of the gut microbiota in nutrition and health', BMJ, 361, k2179.",
        url: "https://www.bmj.com/content/361/bmj.k2179"
      },
      {
        label:
          "Carabotti, M., Scirocco, A., Maselli, M.A. and Severi, C. (2015) 'The gut-brain axis: interactions between enteric microbiota, central and enteric nervous systems', Annals of Gastroenterology, 28(2), pp. 203-209.",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4367209/"
      },
      {
        label:
          "Conlon, M.A. and Bird, A.R. (2015) 'The impact of diet and lifestyle on gut microbiota and human health', Nutrients, 7(1), pp. 17-44.",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4303825/"
      },
      {
        label:
          "Monda, V. et al. (2017) 'Exercise modifies the gut microbiota with positive health effects', Oxidative Medicine and Cellular Longevity, 2017.",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5357536/"
      },
      {
        label:
          "Hou, K. et al. (2022) 'Microbiota in health and diseases', Signal Transduction and Targeted Therapy, 7, p. 135.",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8754568/"
      },
      {
        label:
          "Ahmed, F. et al. (2025) 'Relationship between stress, diet, and gut microbiota: a cross-sectional study', PMC, PMID: 41102818.",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12532885/"
      }
    ]
  },
  {
    slug: "understanding-chronic-constipation-causes-diet-solutions",
    title: "Understanding Chronic Constipation: Causes, Diet & Evidence-Based Solutions",
    excerpt:
      "Chronic constipation is common, but the right support depends on the cause. Learn about fibre, hydration and evidence-based dietary strategies.",
    image: "/chronic-constipation-cover.png",
    category: "Constipation",
    date: "2026-06-14",
    readingTime: "5 min read",
    author: "Iballa Martinez",
    callout:
      "Constipation can be diet-related, but it can also be linked to medications, hormones, mobility, medical conditions and bowel disorders, so persistent symptoms deserve proper assessment.",
    sections: [
      {
        heading: "What Is Chronic Constipation?",
        body: [
          "Chronic constipation is characterised by infrequent bowel movements and/or stools that are hard to pass and may require straining, that persist over time. The Bristol Stool Chart shows different stool consistencies, with Type 1 and Type 2 being most commonly associated with constipation."
        ]
      },
      {
        heading: "Who Is Most Affected?",
        body: [
          "It is estimated that chronic constipation affects 10-16% of people globally. It is more common among adults over the age of 65 and among women, particularly during pregnancy and menopause due to hormonal changes."
        ]
      },
      {
        heading: "What Causes Constipation?",
        body: [
          "Constipation can be diet-related, however there are also many non-dietary factors that can contribute."
        ],
        subsections: [
          {
            heading: "Diet-related causes",
            list: ["Low fibre diet", "Low fluid intake"]
          },
          {
            heading: "Non-dietary causes",
            list: [
              "Poor mobility and sedentary lifestyle",
              "Hormonal changes",
              "Medications",
              "Metabolic conditions",
              "Neurological conditions",
              "Mechanical causes",
              "Colorectal cancer",
              "Diverticular disease",
              "Functional gut disorders",
              "Socioeconomic background",
              "Mental health",
              "Anorectal disorders"
            ]
          }
        ]
      },
      {
        heading: "The Role of Fibre in Managing Constipation",
        body: [
          "It is recommended that adults consume at least 30 grams of fibre per day, yet it is estimated that 90% of people do not meet this target. If you are looking to increase your fibre intake, it is important to do so gradually to avoid discomfort and gut-related symptoms.",
          [
            "Increasing fibre intake may help relieve constipation among individuals with a normal colonic transit time. However, those with slow transit time may experience a worsening of symptoms, as fibre adds bulk to the stool and is fermented by gut bacteria, releasing gas which can cause discomfort. If you want to know more about gut bacteria, ",
            {
              label: "read the gut microbiome article",
              href: "/blog/gut-microbiome-why-your-gut-health-matters"
            },
            "."
          ],
          "Fibre can be broadly classified into two types: soluble and insoluble, and they work in slightly different ways.",
          "Soluble fibre dissolves in water and forms a gel-like substance in the gut, which helps to soften stools. It is found in oats, legumes and flax seeds.",
          "Insoluble fibre, sometimes referred to as the roughage of food, does not dissolve in water. Instead, it adds bulk to the stool, helping it to move through the gut more quickly. Foods rich in insoluble fibre include wholegrain bread and cereals, bran, the skins of fruits and vegetables, and nuts.",
          "It is worth noting that most foods contain a combination of both soluble and insoluble fibre rather than just one type, and both play an important role in digestive health. When it comes to fibre, variety is key.",
          "Here is a table summarizing the content of fibre per serving of some high fibre foods:"
        ],
        table: {
          headers: ["Food", "Serving", "Fibre in grams"],
          rows: [
            ["Avocado", "1/2 medium (75g)", "5.0"],
            ["Apple (with skin)", "1 medium (182g)", "4.4"],
            ["Brussels sprouts (cooked)", "1 cup (156g)", "4.1"],
            ["Oats (rolled, dry)", "1/2 cup (40g)", "4.0"],
            ["Sweet potato (baked)", "1 medium (130g)", "3.8"],
            ["Carrots (raw)", "1 cup (128g)", "3.6"],
            ["Almonds", "1 oz (28g)", "3.5"],
            ["Popcorn (air-popped)", "3 cups (24g)", "3.5"],
            ["Sunflower seeds", "1/4 cup (32g)", "3.1"],
            ["Mango", "1 cup (165g)", "2.6"],
            ["Kale (cooked)", "1 cup (130g)", "2.6"],
            ["Whole wheat bread", "1 slice (28g)", "1.9"],
            ["Pumpkin seeds", "1 oz (28g)", "1.8"]
          ]
        }
      },
      {
        heading: "Don't Forget Hydration",
        body: [
          "Staying well hydrated is a key part of managing constipation, as fluids help to keep stools soft and the bowel lubricated. As a general guide, most adults need between 30-35 mls per kg of body weight per day, which for most people amounts to around 2 litres. Water is the best source of hydration, however hot drinks, milk, juice, smoothies, and high water-content fruits and vegetables also contribute to your daily fluid intake.",
          "For most people, water is sufficient and electrolyte drinks are not necessary unless you are in a very hot climate or recovering from prolonged intense exercise such as running or cycling."
        ]
      },
      {
        heading: "Evidence-Based Interventions for Constipation",
        body: [
          "The British Dietetic Association recently published comprehensive evidence-based guidelines on the dietary management of chronic constipation. Here is a summary of their key findings."
        ],
        subsections: [
          {
            heading: "Psyllium husk",
            body: [
              "Psyllium husk, also known as ispaghula husk, is a well-studied form of soluble fibre that acts as a gentle laxative by forming a gel-like substance in the gut, absorbing water and increasing stool bulk. Research has shown that psyllium husk supplements can soften stools, reduce straining, and increase bowel frequency in those with constipation. It is important to take these supplements with plenty of water and to introduce them gradually."
            ]
          },
          {
            heading: "Probiotics",
            body: [
              "Probiotics may help to ease some symptoms of constipation, however the specific strains that are most beneficial are not yet known and this area requires further research. It may therefore be worth trialling a probiotic for approximately 12 weeks and monitoring symptoms. If no improvement is noted, the probiotic can be discontinued. When it comes to probiotics, it is important to note that a higher price does not mean it is better."
            ]
          },
          {
            heading: "Kiwi fruit",
            body: [
              "Kiwi fruit has been shown to help with constipation by softening stools and increasing bowel frequency, similarly to psyllium husk. Including two green kiwis per day is a simple and practical way to help manage symptoms."
            ]
          },
          {
            heading: "Rye bread",
            body: [
              "Rye bread has been found to be more effective than regular bread at increasing stool frequency in constipation. It is worth noting, however, that the studies in this area used large amounts, approximately four slices a day, of traditionally made rye bread, which may not be readily accessible or realistic for most people."
            ]
          },
          {
            heading: "Magnesium oxide",
            body: [
              "Magnesium oxide supplements have been found to ease symptoms of constipation by increasing bowel frequency, softening stools, reducing straining, and decreasing the severity of bloating and abdominal pain. Please consult a medical professional before taking magnesium supplements if you have kidney disease."
            ]
          },
          {
            heading: "High mineral water",
            body: [
              "High mineral water has shown clinical benefits for people with constipation when compared to low mineral water. However, water with the mineral levels used in the studies is not yet widely available across many European countries. This may also not be suitable for those with kidney disease."
            ]
          }
        ]
      },
      {
        heading: "When to See a Doctor: Red Flag Symptoms",
        body: [
          "Please speak to a medical professional as soon as possible if you experience any of the following:"
        ],
        list: [
          "Changes in bowel habit after the age of 50",
          "Sudden onset of constipation in an older individual",
          "Blood in the stool or toilet bowl",
          "Unintentional weight loss",
          "Low iron levels",
          "Personal or family history of colorectal cancer",
          "Waking at night to pass a bowel movement",
          "Incontinence",
          "Pale stools",
          "Mucus in the stool",
          "Severe abdominal pain"
        ]
      }
    ],
    references: [
      {
        label:
          "Forootan, M., Bagheri, N. and Darvishi, M. (2018) 'Chronic constipation: a review of literature', Medicine, 97(20), e10631.",
        url: "https://doi.org/10.1097/MD.0000000000010631"
      },
      {
        label:
          "Dimidi, E. et al. (2025) 'British Dietetic Association guidelines for the dietary management of chronic constipation in adults', Journal of Human Nutrition and Dietetics, 38(5), e70133.",
        url: "https://doi.org/10.1111/jhn.70133"
      }
    ]
  },
  {
    slug: "pcos-new-name-pmos-polyendocrine-metabolic-ovarian-syndrome",
    title: "PCOS Got a New Name: Meet PMOS, the New Kid on the Block",
    excerpt:
      "PCOS has officially been renamed PMOS to better reflect the hormonal, metabolic and ovarian features of this complex condition.",
    image: "/pmos-pcos-cover.png",
    category: "PMOS",
    date: "2026-07-28",
    readingTime: "8 min read",
    author: "Iballa Martinez",
    callout:
      "PMOS is about much more than the ovaries. The new name reflects the hormonal and metabolic systems involved, while keeping ovarian dysfunction in the picture.",
    sections: [
      {
        heading: "Why the Name Changed",
        body: [
          "On May 12th of this year, the name of the condition PCOS, or polycystic ovary syndrome, was officially changed to PMOS: polyendocrine metabolic ovarian syndrome. This change was made to reflect the complexity of a condition that an estimated 170 million women of reproductive age live with worldwide.",
          "Let's break down the reasoning behind the new name."
        ],
        subsections: [
          {
            heading: "Polyendocrine",
            body: [
              "Poly means many, and endocrine refers to hormones and their pathways. This highlights that PMOS affects many hormonal systems in the body, causing disturbances that include increased release of androgens by the ovaries and adrenal glands, insulin resistance leading to increased insulin release by the pancreas, and disruptions in the hormonal pathway between the hypothalamus in the brain and the ovaries."
            ]
          },
          {
            heading: "Metabolic",
            body: [
              "Metabolic abnormalities are glitches in how the body processes things like blood sugar and cholesterol, and they are inherent to PMOS. These include insulin resistance, obesity, type 2 diabetes, high cholesterol, high blood pressure, and increased risk of heart disease. That is why metabolic was added to the name."
            ]
          },
          {
            heading: "Ovarian",
            body: [
              "Not all women diagnosed with PMOS will actually have cysts on their ovaries on examination, which is exactly why the old name, PCOS, caused confusion for so many years. Still, ovarian dysfunction and the presence of cysts, which are actually immature ovarian follicles, remain part of the diagnostic criteria, so a reference to the ovaries was kept in the name."
            ]
          },
          {
            heading: "Syndrome",
            body: [
              "The word syndrome is unchanged. By definition, a syndrome is a variable collection of signs and symptoms that occur together in a specific condition. In this case, PMOS. The exact underlying mechanisms and how the condition presents can vary greatly from person to person."
            ]
          }
        ]
      },
      {
        heading: "How Is PMOS Diagnosed?",
        body: [
          "The diagnostic process has not changed with the name change. What has changed is awareness that the new name highlights that this condition is about much more than just the ovaries.",
          "PMOS is diagnosed using the Rotterdam criteria. To receive a diagnosis, an individual must meet at least 2 of the following 3 criteria, and must be an adult, 20 years of age or older. Different criteria may apply for teens."
        ],
        list: [
          "Irregular or absent periods",
          "Excess production of male hormones, also known as hyperandrogenism, on clinical examination or blood results. This may present as excess hair growth on the face, chest, or back; facial and/or body acne; hair thinning or male-pattern hair loss; or elevated testosterone levels on a blood test.",
          "Presence of immature ovarian follicles, commonly referred to as cysts, on ultrasound."
        ]
      },
      {
        heading: "Diet and PMOS",
        body: [
          "The 2023 International Guidelines for the management of PMOS recommend lifestyle management as first-line treatment for all women with the condition, and that includes dietary changes.",
          "While no single diet composition has been proven superior to another, there are many dietary changes women living with PMOS can make to support their overall health.",
          "It is important to note that many women are told weight loss will help manage PMOS. But the reality is that weight loss may be very difficult for women with PMOS. The exact mechanisms are not fully understood, but it is recognised that many women with PMOS have underlying processes that drive greater weight gain and higher BMI, making sustainable weight loss extremely challenging for some.",
          "That is why it is important to emphasise that lifestyle changes support overall health and have a positive impact regardless of whether weight loss occurs. Healthcare professionals should communicate this clearly to all women living with PMOS, in order to minimise weight stigma. It is also worth noting that the prevalence of disordered eating and eating disorders is higher among women living with PMOS, so lifestyle advice should be approached with kindness, respect, and always with the goal of supporting a positive relationship with food.",
          "Even though no single diet composition is proven to outperform others for managing PMOS, there are many changes women can make to support their health and manage symptoms. Here are some examples."
        ]
      },
      {
        heading: "Prioritise Slow-Release Carbohydrates",
        body: [
          "Insulin resistance affects approximately 75% of those living with PMOS, so it is worth understanding what insulin resistance actually is.",
          "Think of your cells as doors, and insulin, the hormone your pancreas releases in response to raised blood glucose, as the key. Blood glucose rises after eating, especially after eating carbohydrates. Glucose is the body's energy currency and the preferred fuel source for our cells, tissues and organs. Insulin acts as the key that opens the cell door to let glucose in. But with insulin resistance, the lock is a bit rusty. Insulin struggles to let glucose into the cells, so extra glucose keeps circulating in the bloodstream, and the pancreas releases more and more insulin trying to bring levels down.",
          "Ongoing insulin resistance and the resulting hyperinsulinaemia increase the risk of developing type 2 diabetes. In those with PMOS, it also drives androgen overproduction, which leads to irregular periods, acne, facial hair and other symptoms linked to raised testosterone levels."
        ]
      },
      {
        heading: "Include Omega-3 in the Diet",
        body: [
          "PMOS is associated with chronic, low-grade inflammation in the body, which increases the risk of further insulin resistance, type 2 diabetes and heart disease. Omega-3 is a type of essential fatty acid well known for its anti-inflammatory properties. While including omega-3 in the diet will not solve every issue, it is a solid dietary strategy to help reduce that low-grade inflammation while supporting hormonal, brain and heart health.",
          "Oily fish is the best source of omega-3, as it contains EPA and DHA, two forms of omega-3 that the body can use efficiently. Good sources include salmon, trout, mackerel, sardines and tuna. Eating oily fish twice a week should provide enough omega-3.",
          "Other dietary sources of omega-3 include seeds and nuts, which contain mostly ALA, a form of omega-3 the body does not utilise as efficiently as EPA and DHA. Still, they are worth including in the diet, as they offer many other nutrients and support overall health. If you do not eat fish, whether due to preference or because you follow a plant-based diet, an omega-3 supplement may be worth considering."
        ]
      },
      {
        heading: "Fill Your Plate with Colour",
        body: [
          "Aim for as much colour as possible on your plate in the form of fruit and vegetables. This adds fibre and polyphenols, which are compounds with anti-inflammatory properties. Aiming for half your plate at mealtimes to be fruit and veg will help ensure you get enough fibre throughout the day, which further slows the release of glucose into the bloodstream after meals.",
          "It also supports weight management by keeping you full for longer, and feeds your gut bacteria, which further supports appetite regulation and metabolism through the production of short-chain fatty acids. These, along with polyphenols, also help reduce inflammation."
        ]
      },
      {
        heading: "Strength Training",
        body: [
          "Although not directly linked to diet, strength training supports health in those with PMOS by altering body composition and increasing muscle mass, which is more metabolically active than fat tissue, and by improving insulin resistance. Two strength-training sessions a week may be enough to see health benefits."
        ]
      },
      {
        heading: "Inositol Supplementation",
        body: [
          "Inositol has been shown in some studies to be as effective as metformin at reducing insulin resistance. It has also been shown to improve lipid profile and rates of gestational diabetes, reduce androgen levels, reduce cravings and help regulate menstrual cycles. If you are considering starting inositol, always check with a medical provider first about dosage, side effects and contraindications, for example starting it at the same time as metformin."
        ]
      },
      {
        heading: "In Summary",
        body: [
          "PMOS is a complex condition involving many moving parts, and the same is true when it comes to managing it through nutrition. The recommendations above are by no means the only changes you can make. There is further support out there.",
          "It is also worth noting that the advice above is necessarily generic. For nutrition support tailored to your own body, symptoms and lifestyle, it is always best to work with a registered dietitian.",
          [
            "If you have PMOS and you are ready to feel more in control of your lifestyle, nutrition, and symptoms, I would love to help. ",
            {
              label: "Book an appointment here",
              href: "https://calendly.com/iballa-mtzyanes/initial-consultation"
            },
            " and let's build a plan that actually works for your body."
          ]
        ]
      }
    ],
    references: [
      {
        label:
          "Teede, H. et al. International Evidence-Based Guideline for the Assessment and Management of Polycystic Ovary Syndrome (PMOS) 2023, Monash University, updated 12 June 2026.",
        url: "https://www.monash.edu/__data/assets/pdf_file/0009/4360095/Updated-PMOS-Guideline-12-June26.pdf"
      },
      {
        label:
          "Cree, M.G. et al. (2026) 'Polyendocrine Metabolic Ovarian Syndrome, the New Name for Polycystic Ovary Syndrome: A Multistep Global Consensus Process', The Lancet, 407.",
        url: "https://doi.org/10.1016/S0140-6736(26)00717-8"
      },
      {
        label:
          "Szkodziak, P. et al. (2025) 'Insulin Resistance in Polycystic Ovary Syndrome Phenotypes and the Vicious Cycle Model in Its Etiology', Scientific Reports, 15, 42649.",
        url: "https://doi.org/10.1038/s41598-025-26718-2"
      },
      {
        label:
          "Armanini, D., Boscaro, M., Bordin, L. and Sabbadin, C. (2022) 'Controversies in the Pathogenesis, Diagnosis and Treatment of PCOS: Focus on Insulin Resistance, Inflammation, and Hyperandrogenism', International Journal of Molecular Sciences, 23(8), 4110.",
        url: "https://doi.org/10.3390/ijms23084110"
      },
      {
        label:
          "Duan, M. et al. (2026) 'Effects of Inositol in Women with Polycystic Ovary Syndrome: An Umbrella Review of Meta-Analyses from Randomized Controlled Trials', Frontiers in Endocrinology, 17, 1741509.",
        url: "https://doi.org/10.3389/fendo.2026.1741509"
      }
    ]
  },
  glp1BlogPost
];

export function getBlogPost(slug) {
  return blogPosts.find((post) => post.slug === slug);
}
